/**
 * CI design tokens -> :root CSS override.
 * Emits ONLY explicitly set variables; defaults live in
 * public/assets/css/wm-tokens.css and as var() fallbacks at every usage site.
 * PHP mirror: files/wps/api_php/publishedData.php (wm_generateTokenCss) —
 * keep the mapping tables there identical. The CI design round-1 fields
 * (contentWidth, spacingCustom, radiusCustom, radiusOverrides) ride on the
 * same mirror rule: their PHP mapping tables must be kept identical just
 * like the pre-existing fields' ones (guarded by ciTokensPhpParity.spec.ts).
 * The lookup tables below now come from the DTCG manifest (wm.tokens.json)
 * via wmTokensManifest.js — that manifest is the single source of defaults.
 *
 * Plain JS (not TS, persist-once spec 2026-08-18-token-css-persist-once
 * Task 2): tokenCssPersist.js's withPersistedTokenCss() must resolve inside
 * the Vue-2 webpack build (ProjectSettings.vue/FolderSettings.vue import it),
 * which cannot bundle .ts files — precedent: cssEmitter.js, elementSchemas.js.
 * Types live in ciTokens.d.ts.
 */

import {
  getSpacePresets, getSpaceStepFactors, getContentWidths,
  getContentStepFactors, getRadiusScale, getShadowScale, getManifestCssValue,
} from './wmTokensManifest';
import {
  contrastRatio, contrastTextColor, contrastTextColorAA, parseColorToRgb, relativeLuminance,
} from './wcagContrast';

// Single source: the DTCG manifest (wm.tokens.json). These consts keep their
// old names so every usage site below stays untouched; the PHP mirror's
// tables are unchanged and now chain to the manifest transitively
// (PHP <-> ciTokens via the parity guards, ciTokens <-> manifest via import).
const SPACING = getSpacePresets();
const SPACE_STEP_FACTOR = getSpaceStepFactors();
const CONTENT_WIDTH_PX = getContentWidths();
const CONTENT_STEP_FACTOR = getContentStepFactors();
const RADIUS = getRadiusScale();
const SHADOW = getShadowScale();

/** Nur #hex, rgb()/rgba(), hsl()/hsla() — alles andere wird verworfen. */
const COLOR_RE = /^(#[0-9a-fA-F]{3,8}|(?:rgb|hsl)a?\([0-9.,%\s/]+\))$/;

function safeColor(value) {
  return typeof value === 'string' && COLOR_RE.test(value.trim()) ? value.trim() : null;
}

// Adaptive text-contrast token for --wm-color-primary: WCAG relative-
// luminance formula, see wcagContrast.js and
// docs/superpowers/specs/2026-08-18-color-round-design.md §A1/A2. PHP
// mirror: publishedData.php's wm_contrastTextColor — mapping identical
// (ciTokensPhpParity.spec.ts / ciTokensPhpRuntimeParity.spec.ts).

// Whitelist guards — invalid input emits NOTHING (the UI clamps while
// typing; the generator rejects, mirroring the CSS emitter's philosophy).
function isValidContentWidth(v) {
  return typeof v === 'number' && Number.isInteger(v) && v >= 480 && v <= 2000;
}
function isValidSpacingCustom(v) {
  return typeof v === 'number' && Number.isFinite(v) && v >= 0.1 && v <= 1;
}
function isValidRadiusCustom(v) {
  return typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 100;
}

// Button padding in px (founder 30.09.2026: v3 buttons only via tokens).
// Default = the theme's $btn-padding-y/-x (0.3125rem 1rem).
export const BUTTON_PADDING_DEFAULT = Object.freeze({ y: 5, x: 16 });
export const BUTTON_PADDING_MAX = Object.freeze({ y: 48, x: 96 });
function isValidButtonPadding(p) {
  const ok = (v, max) => Number.isInteger(v) && v >= 0 && v <= max;
  return !!p && ok(p.y, BUTTON_PADDING_MAX.y) && ok(p.x, BUTTON_PADDING_MAX.x);
}
/** Stored padding when valid, else the theme default ({ y, x } in px). */
export function resolveButtonPadding(designTokens) {
  const p = designTokens?.buttonPadding;
  return isValidButtonPadding(p) ? { y: p.y, x: p.x } : { ...BUTTON_PADDING_DEFAULT };
}

// Card shadow (spec 2026-10-03 EVZ Paket 7 E1): numbers, not free text, so no
// CSS can be smuggled in. Sets --wm-shadow-2, which every v3 surface reads.
// The default is the theme's --wm-shadow-2 (wm-tokens.css).
export const CARD_SHADOW_MAX = Object.freeze({ y: 32, blur: 64, opacity: 40 });
export const CARD_SHADOW_DEFAULT = Object.freeze({
  color: '#000000', y: 4, blur: 12, opacity: 12,
});
function isValidCardShadow(s) {
  const ok = (v, max) => Number.isInteger(v) && v >= 0 && v <= max;
  return !!s && ok(s.y, CARD_SHADOW_MAX.y) && ok(s.blur, CARD_SHADOW_MAX.blur) && ok(s.opacity, CARD_SHADOW_MAX.opacity);
}
/** Stored card shadow when valid, else the theme default ({ color, y, blur, opacity }). */
export function resolveCardShadow(designTokens) {
  const s = designTokens?.cardShadow;
  return isValidCardShadow(s)
    ? {
      color: safeColor(s.color) || CARD_SHADOW_DEFAULT.color, y: s.y, blur: s.blur, opacity: s.opacity,
    }
    : { ...CARD_SHADOW_DEFAULT };
}
function cardShadowCss({
  color, y, blur, opacity,
}) {
  const { r, g, b } = parseColorToRgb(color) || { r: 0, g: 0, b: 0 };
  return `0 ${y}px ${blur}px rgba(${r}, ${g}, ${b}, ${opacity / 100})`;
}
// Radius per type: a stage (string) as before, or px as a number (Paket 7 E2).
function radiusOverrideValue(val) {
  // includes() compares strictly: a malformed ['l'] is no stage (PHP is_string guard).
  if (Object.keys(RADIUS).includes(val)) return RADIUS[val];
  return isValidRadiusCustom(val) ? `${val}px` : null;
}

/**
 * Resolves designTokens to the values the emitter would produce, for the CI
 * settings preview. Same tables and precedence as generateTokenCss (custom
 * beats preset; invalid input falls back); unset = the theme defaults.
 */
export function resolveDesignTokenPreview(designTokens) {
  const t = designTokens || {};
  const spaceBase = isValidSpacingCustom(t.spacingCustom)
    ? t.spacingCustom : parseFloat(SPACING[t.spacing] || getManifestCssValue('--wm-space'));
  const rem = (step) => `${Math.round(spaceBase * SPACE_STEP_FACTOR[step] * 1000) / 1000}rem`;
  const baseRadius = isValidRadiusCustom(t.radiusCustom)
    ? `${t.radiusCustom}px` : (RADIUS[t.radius] ?? getManifestCssValue('--wm-radius'));
  const over = (key) => radiusOverrideValue(t.radiusOverrides?.[key]) ?? baseRadius;
  return {
    space: { s: rem('s'), m: rem('m'), l: rem('l') },
    radius: {
      card: over('card'), image: over('image'), button: over('button'), box: over('box'),
    },
    shadow: SHADOW[t.shadow] ?? getManifestCssValue('--wm-shadow'),
    shadowCard: cardShadowCss(resolveCardShadow(t)),
    contentWidth: isValidContentWidth(t.contentWidth)
      ? t.contentWidth : parseInt(CONTENT_WIDTH_PX.xl, 10),
    buttonPadding: (({ y, x }) => `${y}px ${x}px`)(resolveButtonPadding(t)),
  };
}

// --- Typografie-Runde (Spec 2026-08-18-typography-round-design.md §1/§2) ---
// Feste Rollen-Reihenfolge (bindend, Emissions- und Panel-Ordnung).
export const TYPOGRAPHY_ROLES = Object.freeze(['display', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'lead', 'body', 'caption', 'button']);
// One fixed curve (spec 2026-09-29): customers bring a CI, not a style
// choice. Values are the former "editorial" preset; they only fill roles the
// CI leaves empty. min/max are rem anchors at 375px/1200px, lh unitless,
// ls in em (display/h1/h2 only).
// Deep-frozen: the settings UI exposes it in Vue data(), which must not make it reactive.
export const TYPE_CURVE = Object.freeze(Object.fromEntries(Object.entries({
  display: {
    min: 2.25, max: 3.75, lh: 1.05, ls: -0.02,
  },
  h1: {
    min: 1.75, max: 2.75, lh: 1.15, ls: -0.01,
  },
  h2: {
    min: 1.5, max: 2.0625, lh: 1.25, ls: -0.005,
  },
  h3: { min: 1.25, max: 1.625, lh: 1.3 },
  h4: { min: 1.125, max: 1.3125, lh: 1.35 },
  h5: { min: 1, max: 1.125, lh: 1.4 },
  h6: { min: 0.875, max: 1, lh: 1.4 },
  lead: { min: 1.125, max: 1.3125, lh: 1.55 },
  body: { min: 1, max: 1.0625, lh: 1.65 },
  caption: { min: 0.875, max: 0.875, lh: 1.5 },
  button: { min: 1, max: 1, lh: 1.2 },
}).map(([k, v]) => [k, Object.freeze(v)])));

// Discard-not-clamp bounds, shared with the settings UI (which clamps).
export const TYPE_LIMITS = Object.freeze({
  size: Object.freeze({ min: 0.75, max: 10 }),
  lineHeight: Object.freeze({ min: 0.1, max: 3 }),
  ls: Object.freeze({ min: -0.1, max: 0.3 }),
});
export const TYPE_WEIGHTS = Object.freeze([100, 200, 300, 400, 500, 600, 700, 800, 900]);

function isValidTypeAnchor(v) {
  return typeof v === 'number' && Number.isFinite(v) && v >= TYPE_LIMITS.size.min && v <= TYPE_LIMITS.size.max;
}
function isValidLineHeightOverride(v) {
  return typeof v === 'number' && Number.isFinite(v) && v > 0 && v <= TYPE_LIMITS.lineHeight.max;
}
function isValidLetterSpacing(v) {
  return typeof v === 'number' && Number.isFinite(v) && v >= TYPE_LIMITS.ls.min && v <= TYPE_LIMITS.ls.max;
}

const FONT_GENERIC_WHITELIST = ['serif', 'sans-serif', 'monospace'];
function safeFontGeneric(v) {
  return FONT_GENERIC_WHITELIST.includes(v) ? v : 'sans-serif';
}
// Font-Namen kommen laut Datenmodell aus der bestehenden Font-Maschinerie
// (Select, keine Freitext-Eingabe im UI), werden hier trotzdem gegen
// CSS-Injection abgesichert, weil sie unquotiert in font-family landen
// wuerden: keine Anfuehrungszeichen/Backslash/Klammern/Semikolon.
const FONT_NAME_RE = /^[^'"\\{};<>]{1,100}$/;
function safeFontName(v) {
  if (typeof v !== 'string') return null;
  const trimmed = v.trim();
  return trimmed && FONT_NAME_RE.test(trimmed) ? trimmed : null;
}
function fontFamilyValue(fontName, generic) {
  const safeName = safeFontName(fontName);
  return safeName ? `'${safeName}', ${safeFontGeneric(generic)}` : safeFontGeneric(generic);
}

// Serialisierung (Spec §2 bindend): max. 4 Nachkommastellen, trailing zeros
// gekuerzt, ASCII-Minus (JS' Number->String liefert bereits U+002D).
function fmtTypeNum(n) { return String(parseFloat(n.toFixed(4))); }

// Fliessende Groesse (Spec §2 bindend): Anker MIN@375px / MAX@1200px in rem;
// 375px = 23.4375rem (bei 16px-Basis), Spanne bis 1200px = 51.5625rem.
// Dimensional korrekt: Laenge/Zahl = Laenge, Zahl x Laenge = Laenge.
// Bekannter, akzeptierter Randfehler: 100vw zaehlt die Scrollbar mit
// (Desktop ~15px) und liegt damit minimal ueber der sichtbaren Breite —
// Groessenfehler < 0.3%, siehe Spec §2.
// MIN >= MAX (z.B. caption/button-Kurven mit min===max, oder ein
// entarteter Override) ergibt einen festen Wert statt eines clamp().
export function buildClampSize(min, max) {
  if (min >= max) return `${fmtTypeNum(max)}rem`;
  return `clamp(${fmtTypeNum(min)}rem, calc(${fmtTypeNum(min)}rem + ${fmtTypeNum(max - min)} * ((100vw - 23.4375rem) / 51.5625)), ${fmtTypeNum(max)}rem)`;
}

/**
 * MIN/MAX/LH/(LS) for one role: each value the CI sets wins on its own,
 * the curve fills the rest. Invalid values are discarded, never clamped.
 */
export function resolveTypeAnchors(typo, role) {
  const curve = TYPE_CURVE[role];
  const o = typo?.overrides?.[role] || {};
  const result = {
    min: isValidTypeAnchor(o.min) ? o.min : curve.min,
    max: isValidTypeAnchor(o.max) ? o.max : curve.max,
    lh: isValidLineHeightOverride(o.lineHeight) ? o.lineHeight : curve.lh,
  };
  const ls = isValidLetterSpacing(o.ls) ? o.ls : curve.ls;
  if (ls !== undefined) result.ls = ls;
  return result;
}

// Allowed values for the display face; anything else is ignored (the CSS
// must stay valid whatever a stored CI holds).
const DISPLAY_STYLE = { normal: 'normal', italic: 'italic' };
const DISPLAY_STRETCH = { normal: 'normal', condensed: 'condensed', expanded: 'expanded' };
const DISPLAY_TRANSFORM = { none: 'none', uppercase: 'uppercase' };

// Where a text role lives in the theme (spec 2026-09-24 Textstil §3.2): stronger than
// the theme's own rules, weaker than the v2 components (.v2-component x3). Bereich
// elements are left out, their levels read the role's variables (Textarten §3.3).
const OHNE_BEREICH = ':where(:not(.wm-section *))';
const ROLE_SELECTORS = {
  h1: `html.wm-typo .pgs-article h1${OHNE_BEREICH}`,
  h2: `html.wm-typo .pgs-article h2${OHNE_BEREICH}`,
  h3: `html.wm-typo .pgs-article h3${OHNE_BEREICH}`,
  h4: `html.wm-typo .pgs-article h4${OHNE_BEREICH}`,
  h5: `html.wm-typo .pgs-article h5${OHNE_BEREICH}`,
  h6: `html.wm-typo .pgs-article h6${OHNE_BEREICH}`,
  lead: 'html.wm-typo .lead',
  body: `html.wm-typo .pgs-article :is(p, li, td)${OHNE_BEREICH}`,
  caption: 'html.wm-typo.wm-typo figcaption',
};

/** The roles a CI can give italic/weight/capitals: display only as variables (a Bereich level). */
export const ROLE_STYLE_ROLES = Object.freeze(['display', ...Object.keys(ROLE_SELECTORS), 'button']);

function roleStyleDecls(stil) {
  const decl = [];
  if (typeof stil?.kursiv === 'boolean') decl.push(['style', stil.kursiv ? 'italic' : 'normal']);
  if (TYPE_WEIGHTS.includes(stil?.staerke)) decl.push(['weight', stil.staerke]);
  if (typeof stil?.gross === 'boolean') decl.push(['transform', stil.gross ? 'uppercase' : 'none']);
  return decl;
}

const DISPLAY_ROLES = ['display', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'];

/** Font stack a role's own font (roleStyles[role].schrift) gives, or '' (role keeps the display/text font). */
function roleFamily(role, stil) {
  const name = safeFontName(stil?.schrift);
  if (!name) return '';
  return `'${name}', var(--wm-font-${DISPLAY_ROLES.includes(role) ? 'display' : 'text'})`;
}

const PROP = { style: 'font-style', weight: 'font-weight', transform: 'text-transform' };

/**
 * Italic, weight and capitals per text role: a real rule for what the CI set,
 * nothing for the rest. A var() fallback cannot say "leave it", it would
 * overwrite today's look (the 600/bold mix of the headings).
 */
function roleStyleRules(typo) {
  return Object.entries(ROLE_SELECTORS).map(([role, selector]) => {
    const decl = roleStyleDecls(typo?.roleStyles?.[role]).map(([k, v]) => `${PROP[k]}: ${v};`);
    if (roleFamily(role, typo?.roleStyles?.[role])) decl.push(`font-family: var(--wm-type-${role}-family);`);
    return decl.length ? `${selector} { ${decl.join(' ')} }` : '';
  }).filter(Boolean);
}

// --- Custom text styles (spec 2026-10-15 §2/§4) ---
// The id is the only string that enters a selector or variable name, so the
// emitter checks it here, not only the UI.
export const CUSTOM_STYLE_ID_RE = /^[a-z0-9-]{1,30}$/;
export const CUSTOM_STYLES_MAX = 20;

/** Font stack of a custom style: the CI's display/text font or a font name in front of the text font. */
function customStyleFamily(font) {
  if (font === 'display' || font === 'text') return `var(--wm-font-${font})`;
  const name = safeFontName(font);
  return name ? `'${name}', var(--wm-font-text)` : null;
}

/** One stored style with only its valid fields, or null when id or name is invalid. */
function cleanCustomStyle(s) {
  if (!(s instanceof Object) || String(s.id) !== s.id || !CUSTOM_STYLE_ID_RE.test(s.id)) return null;
  const name = String(s.name) === s.name ? s.name.trim() : '';
  if (!name || name.length > 40) return null;
  const out = { id: s.id, name };
  if (customStyleFamily(s.font)) out.font = s.font;
  if (isValidTypeAnchor(s.min) && isValidTypeAnchor(s.max)) Object.assign(out, { min: s.min, max: s.max });
  if (isValidLineHeightOverride(s.lineHeight)) out.lineHeight = s.lineHeight;
  if (TYPE_WEIGHTS.includes(s.weight)) out.weight = s.weight;
  if (s.italic === true || s.italic === false) out.italic = s.italic;
  if (s.uppercase === true || s.uppercase === false) out.uppercase = s.uppercase;
  if (isValidLetterSpacing(s.ls)) out.ls = s.ls;
  if (safeColor(s.color)) out.color = safeColor(s.color);
  return out;
}

function cleanCustomStyles(list) {
  if (!Array.isArray(list)) return [];
  const seen = new Set();
  return list.map(cleanCustomStyle).filter((s) => {
    if (!s || seen.has(s.id)) return false;
    seen.add(s.id);
    return true;
  }).slice(0, CUSTOM_STYLES_MAX);
}

/** The CI's valid custom text styles in bar order: invalid fields dropped, duplicate ids after the first skipped, at most 20. */
export function customTextStyles(typo) {
  return cleanCustomStyles(typo?.customStyles);
}

// Google font variants the magazine loads (spec 2026-09-29 §1.6): today's
// list never shrinks (Tiptap italics need 400i), chosen weights come on top.
// Copy in files/_app_dev/js/lib.utils.js (wmTypeFontVariants) must stay
// identical, guarded by typeFontVariantsParity.spec.ts.
const BASE_FONT_VARIANTS = ['300', '400', '700', '300i', '400i', '700i'];
export function typeFontVariants(typo) {
  const list = [...BASE_FONT_VARIANTS];
  const on = typo?.enabled === true;
  const styles = (on && typo.roleStyles) || {};
  const weights = [
    ...TYPOGRAPHY_ROLES.map((role) => styles[role]?.staerke),
    ...(on ? customTextStyles(typo).map((s) => s.weight) : []),
  ];
  weights.forEach((w) => {
    if (TYPE_WEIGHTS.includes(w) && !list.includes(String(w))) list.push(String(w), `${w}i`);
  });
  return list.join(',');
}

// Variable suffix -> CSS property, in emission order.
const CUSTOM_PROPS = [
  ['size', 'font-size'], ['lh', 'line-height'], ['weight', 'font-weight'], ['style', 'font-style'],
  ['transform', 'text-transform'], ['ls', 'letter-spacing'], ['family', 'font-family'], ['color', 'color'],
];

function customStyleDecls(s) {
  const d = {};
  if (s.min !== undefined) d.size = buildClampSize(s.min, s.max);
  if (s.lineHeight !== undefined) d.lh = fmtTypeNum(s.lineHeight);
  if (s.weight !== undefined) d.weight = s.weight;
  if (s.italic !== undefined) d.style = s.italic ? 'italic' : 'normal';
  if (s.uppercase !== undefined) d.transform = s.uppercase ? 'uppercase' : 'none';
  if (s.ls !== undefined) d.ls = `${fmtTypeNum(s.ls)}em`;
  if (s.font !== undefined) d.family = customStyleFamily(s.font);
  if (s.color !== undefined) d.color = s.color;
  return CUSTOM_PROPS.filter(([k]) => d[k] !== undefined).map(([k, prop]) => [k, prop, d[k]]);
}

/**
 * Rules for the custom levels. `html` (0,2,1) beats the level rules in
 * wm-elements.scss (0,1,0) and theme `p` rules; not `html.wm-typo`, reader v3
 * never sets that class. The PHP stale graft keeps lines starting with `html`.
 */
function customStyleRules(typo) {
  return customTextStyles(typo).flatMap((s) => {
    const decls = customStyleDecls(s);
    if (!decls.length) return [];
    const sel = `html .wm-el[data-level="custom-${s.id}"]`;
    return [
      `${sel} { ${decls.map(([k, prop, v]) => `${prop}: ${k === 'family' ? v : `var(--wm-type-custom-${s.id}-${k})`};`).join(' ')} }`,
      `${sel} :where(p, li) { font-size: inherit; line-height: inherit; color: inherit; }`,
    ];
  });
}

function emitTypographyLines(typo) {
  const lines = [];
  lines.push('--wm-typo-mode: 1;');
  lines.push(`--wm-font-display: ${fontFamilyValue(typo.displayFont, typo.displayFontGeneric)};`);
  lines.push(`--wm-font-text: ${fontFamilyValue(typo.textFont, typo.textFontGeneric)};`);
  // The look of the headline font, not just its family: a condensed italic
  // uppercase display face is a whole corporate identity on its own
  // (EVZ prototype 2026-09-20). Unknown values fall back to the neutral one,
  // so a broken CI never writes a broken declaration.
  lines.push(`--wm-font-display-style: ${DISPLAY_STYLE[typo.displayStyle] ?? 'normal'};`);
  lines.push(`--wm-font-display-stretch: ${DISPLAY_STRETCH[typo.displayStretch] ?? 'normal'};`);
  lines.push(`--wm-font-display-transform: ${DISPLAY_TRANSFORM[typo.displayTransform] ?? 'none'};`);
  TYPOGRAPHY_ROLES.forEach((role) => {
    const anchors = resolveTypeAnchors(typo, role);
    lines.push(`--wm-type-${role}-size: ${buildClampSize(anchors.min, anchors.max)};`);
    lines.push(`--wm-type-${role}-lh: ${fmtTypeNum(anchors.lh)};`);
    if (anchors.ls !== undefined) lines.push(`--wm-type-${role}-ls: ${fmtTypeNum(anchors.ls)}em;`);
    roleStyleDecls(typo.roleStyles?.[role]).forEach(([k, v]) => lines.push(`--wm-type-${role}-${k}: ${v};`));
    const family = roleFamily(role, typo.roleStyles?.[role]);
    if (family) lines.push(`--wm-type-${role}-family: ${family};`);
  });
  customTextStyles(typo).forEach((s) => {
    // No family variable: var(--wm-font-*) must resolve at the text, where a snippet may set its own font.
    customStyleDecls(s).forEach(([k, , v]) => { if (k !== 'family') lines.push(`--wm-type-custom-${s.id}-${k}: ${v};`); });
  });
  return lines;
}

/** Hue (0..359) and saturation (0..100) of a colour, whole numbers. Mirror: wm_farbtonSaettigung. */
function farbtonSaettigung({ r, g, b }) {
  const [R, G, B] = [r / 255, g / 255, b / 255];
  const max = Math.max(R, G, B);
  const min = Math.min(R, G, B);
  const d = max - min;
  if (d === 0) return [0, 0];
  const l = (max + min) / 2;
  const s = d / (1 - Math.abs(2 * l - 1));
  let h;
  if (max === R) h = (G - B) / d;
  else if (max === G) h = (B - R) / d + 2;
  else h = (R - G) / d + 4;
  h *= 60;
  if (h < 0) h += 360;
  return [Math.round(h) % 360, Math.round(s * 100)];
}

/** HSL (whole degrees and percent) to #rrggbb. Mirror: wm_hslHex. */
function hslHex(h, s, l) {
  const S = s / 100;
  const L = l / 100;
  const c = (1 - Math.abs(2 * L - 1)) * S;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = L - c / 2;
  // One 60 degree sector each: red to yellow, to green, … back to red.
  const [r, g, b] = [[c, x, 0], [x, c, 0], [0, c, x], [0, x, c], [x, 0, c], [c, 0, x]][Math.floor(h / 60)];
  return `#${[r, g, b].map((v) => Math.round((v + m) * 255).toString(16).padStart(2, '0')).join('')}`;
}

/**
 * "Automatisch" for the base tones (feedback 26.09.2026): text and muted
 * text follow the surface, so they stay readable on any surface, with a hint
 * of the brand hue (saturation at most 12 %, muted 8 %). Text sits at 16 % (95 % on a
 * dark surface); muted is the lightest (darkest) step that still reads at
 * 4.5:1. A surface where tinted text fails gets the plain readable colour.
 * Mirror: publishedData.php#wm_autoGrundtoene.
 */
export function autoGrundtoene(surface, primary) {
  const flaeche = parseColorToRgb(surface) || { r: 255, g: 255, b: 255 };
  const lF = relativeLuminance(flaeche);
  const hell = contrastRatio(lF, relativeLuminance({ r: 33, g: 37, b: 41 })) >= contrastRatio(1, lF);
  const marke = primary ? parseColorToRgb(primary) : null;
  const [h, s] = marke ? farbtonSaettigung(marke) : [0, 0];
  const sat = Math.min(s, 12);
  const liest = (hex) => Math.round(contrastRatio(relativeLuminance(parseColorToRgb(hex)), lF) * 100) / 100 >= 4.5;
  const ink = hslHex(h, sat, hell ? 16 : 95);
  if (!liest(ink)) {
    const klar = contrastTextColor(surface || '#ffffff');
    return { ink: klar, muted: klar };
  }
  let muted = ink;
  for (let l = hell ? 17 : 94; hell ? l <= 60 : l >= 40; l += hell ? 1 : -1) {
    const kandidat = hslHex(h, Math.min(s, 8), l);
    if (!liest(kandidat)) break;
    muted = kandidat;
  }
  return { ink, muted };
}

/**
 * Readability guard (spec 2026-10-03 Lesbarkeit §3.3, L2/L3/L5): the token
 * lines derived at WCAG AA. `root` goes at the end of the :root block,
 * `bereich` into one rule right after it. The rule sets the OLD -contrast
 * names, so only Bereiche change (V1/V2 keep the 3:1 values from :root), and
 * pages published before keep their static token css until republished.
 * :where() = specificity 0: customer css and the theme's local overrides win
 * whatever the stylesheet order. New CI lines belong here, not in the PHP
 * graft list. PHP mirror: publishedData.php#wm_derivedTokenCss, appended by
 * wm_appendDerivedLines in wm_getTokenCss.
 */
export const BEREICH_REGEL_SELEKTOR = ':where(.wm-section:not([data-layer]))';

// CI style fields (Lesbarkeit §6.2): the default value is never stored and emits nothing.
// `linie` resolves var(--wm-line) on the Bereich; dark cells read the -dunkel value (wm-cell.scss).
const MISCHUNG = 'color-mix(in srgb, currentColor 18%, transparent)';
export const STIL_ZEILEN = Object.freeze({
  linkStyle: { dezent: ['--wm-link-line: color-mix(in srgb, currentColor 35%, transparent);'] },
  cardStyle: {
    linie: [`--wm-card-shadow: 0 0 0 1px var(--wm-line, ${MISCHUNG});`, `--wm-card-shadow-dark: 0 0 0 1px ${MISCHUNG};`],
    flach: ['--wm-card-shadow: none;', '--wm-card-shadow-dark: none;'],
  },
});

export function derivedTokenCss(ci) {
  const root = [];
  const bereich = [];
  if (!ci) return { root, bereich };
  const aa = (value) => {
    const color = safeColor(value);
    return color ? contrastTextColorAA(color) : null;
  };
  const add = (list, name, value) => {
    if (value) list.push(`${name}: ${value};`);
  };
  const primary = safeColor(ci._customColor?.[0]);
  add(bereich, '--wm-color-primary-contrast', aa(primary));
  const secondary = safeColor(ci._customColor?.[1]);
  add(bereich, '--wm-color-secondary-contrast', aa(secondary));
  // Spec 2026-10-08-artikel-look §2.0: the secondary as large text on the primary surface (figures, kicker),
  // only when it reaches 3:1; else the primary's readable text colour. PHP mirror: publishedData.php.
  if (primary && secondary) {
    const ratio = contrastRatio(relativeLuminance(parseColorToRgb(secondary)), relativeLuminance(parseColorToRgb(primary)));
    add(bereich, '--wm-color-secondary-on-primary-aa', Math.round(ratio * 100) / 100 >= 3 ? secondary : aa(primary));
  }
  (Array.isArray(ci._additionalColors) ? ci._additionalColors : []).forEach((value, i) => {
    add(bereich, `--wm-color-zusatz-${i + 1}-contrast`, aa(value));
  });
  const t = ci.designTokens;
  // The neutrals "Grau"/"Dunkel" of the colour choice: set tones, else the automatic ones.
  let muted = safeColor(t?.colors?.muted);
  let ink = safeColor(t?.colors?.ink);
  if (t?.autoFarben === true && (!muted || !ink)) {
    const auto = autoGrundtoene(safeColor(t.colors?.surface) || '#ffffff', primary);
    muted = muted || auto.muted;
    ink = ink || auto.ink;
  }
  add(bereich, '--wm-color-muted-contrast-aa', aa(muted));
  add(bereich, '--wm-color-ink-contrast-aa', aa(ink));
  // Teil B, only in Bereiche (§6.2, §14): the dark surface with its AA text, link and card style.
  // Key darkSurface, not dark: AI plans and update_ci use colors.dark for the text colour (ink). The text is its own
  // token, read only by the dark surface (wm-cell.scss): overlays on photos keep --v2-on-dark (white).
  const dark = safeColor(t?.colors?.darkSurface);
  const darkText = aa(dark);
  if (darkText) {
    bereich.push(`--v2-dark: ${dark};`, `--wm-color-dark-contrast-aa: ${darkText};`);
  }
  ['linkStyle', 'cardStyle'].forEach((feld) => {
    const wert = t?.[feld];
    // includes() compares strictly: only the listed values, never a prototype key or an array.
    if (Object.keys(STIL_ZEILEN[feld]).includes(wert)) bereich.push(...STIL_ZEILEN[feld][wert]);
  });
  const page = safeColor(t?.colors?.page);
  const pageText = aa(page);
  if (pageText) {
    root.push(`--wm-color-page-contrast-aa: ${pageText};`);
    const primaryRgb = primary ? parseColorToRgb(primary) : null;
    const pageAccent = primaryRgb
      && contrastRatio(relativeLuminance(primaryRgb), relativeLuminance(parseColorToRgb(page))) >= 4.5
      ? primary : pageText;
    root.push(`--wm-color-page-accent-aa: ${pageAccent};`);
  }
  return { root, bereich };
}

const PAGE_RULE = '.pgs-article.pgs-article { background: var(--wm-color-page); --wm-text: var(--wm-text-on-page); color: var(--wm-text); }';

export function generateTokenCss(ci) {
  if (!ci) return '';
  const lines = [];

  const primary = safeColor(ci._customColor?.[0]);
  if (primary) {
    lines.push(`--wm-color-primary: ${primary};`);
    // Kontrast-Token nur wenn die Farbe parsbar ist (hex, rgb(), hsl()).
    // Unparsbare Farben emittieren keinen Kontrast-Token.
    if (parseColorToRgb(primary) !== null) {
      lines.push(`--wm-color-primary-contrast: ${contrastTextColor(primary)};`);
    }
  }
  const secondary = safeColor(ci._customColor?.[1]);
  if (secondary) {
    lines.push(`--wm-color-secondary: ${secondary};`);
    // Kontrast-Token nur wenn die Farbe parsbar ist (hex, rgb(), hsl()).
    // Unparsbare Farben emittieren keinen Kontrast-Token.
    if (parseColorToRgb(secondary) !== null) {
      lines.push(`--wm-color-secondary-contrast: ${contrastTextColor(secondary)};`);
    }
  }
  // Extra CI colours (_additionalColors, spec 2026-09-23 Mischweg §3.5): a
  // Bereich stores `ci.zusatz-<n>`, so a changed CI recolours every place.
  // Mirror: publishedData.php#wm_generateTokenCss.
  (Array.isArray(ci._additionalColors) ? ci._additionalColors : []).forEach((value, i) => {
    const color = safeColor(value);
    if (!color) return;
    lines.push(`--wm-color-zusatz-${i + 1}: ${color};`);
    if (parseColorToRgb(color) !== null) {
      lines.push(`--wm-color-zusatz-${i + 1}-contrast: ${contrastTextColor(color)};`);
    }
  });

  const t = ci.designTokens;
  const hasValidContentWidth = isValidContentWidth(t?.contentWidth);

  if ((t?.spacing && SPACING[t.spacing]) || isValidSpacingCustom(t?.spacingCustom)) {
    const base = isValidSpacingCustom(t?.spacingCustom) ? `${t.spacingCustom}rem` : SPACING[t.spacing];
    lines.push(`--wm-space: ${base};`);
    lines.push('--wm-space-none: 0;');
    ['xs', 's', 'm', 'l', 'xl', '2xl'].forEach((step) => {
      lines.push(`--wm-space-${step}: calc(var(--wm-space, ${base}) * ${SPACE_STEP_FACTOR[step]});`);
    });
    // Static content widths only when no valid contentWidth overrides them —
    // otherwise the derived block below would double-emit --wm-content-*.
    if (!hasValidContentWidth) {
      ['s', 'm', 'l', 'xl', 'xxl'].forEach((size) => {
        lines.push(`--wm-content-${size}: ${CONTENT_WIDTH_PX[size]};`);
      });
    }
  }

  if (hasValidContentWidth) {
    const xl = t.contentWidth;
    ['s', 'm', 'l', 'xl', 'xxl'].forEach((size) => {
      lines.push(`--wm-content-${size}: ${Math.round(xl * CONTENT_STEP_FACTOR[size])}px;`);
    });
  }

  const colorVars = [
    ['surface', '--wm-color-surface'],
    ['muted', '--wm-color-muted'],
    ['accent', '--wm-color-accent'],
    ['ink', '--wm-color-ink'],
    ['page', '--wm-color-page'],
    // JS-only (Paket 7 E3); the PHP fallback grafts it from the stored string.
    ['line', '--wm-color-line'],
  ];
  colorVars.forEach(([key, cssVar]) => {
    const val = safeColor(t?.colors?.[key]);
    if (val) lines.push(`${cssVar}: ${val};`);
  });
  // "Automatisch" (v3 CI view sets autoFarben): unset text and muted follow the surface.
  // Mirror: publishedData.php#wm_generateTokenCss.
  if (t?.autoFarben === true) {
    const ink = safeColor(t.colors?.ink);
    const muted = safeColor(t.colors?.muted);
    if (!ink || !muted) {
      const auto = autoGrundtoene(safeColor(t.colors?.surface) || '#ffffff', primary);
      if (!ink) lines.push(`--wm-color-ink: ${auto.ink};`);
      if (!muted) lines.push(`--wm-color-muted: ${auto.muted};`);
    }
  }
  // The page colour brings its own text colour, the same way the brand
  // colour does: a dark page needs light type outside the cards.
  const page = safeColor(t?.colors?.page);
  if (page && parseColorToRgb(page) !== null) {
    lines.push(`--wm-color-page-contrast: ${contrastTextColor(page)};`);
    // Accent text sitting on the page (kickers, labels, rules): the brand
    // colour when it stands out from the page (3:1, like a large label),
    // else the page's own text colour — a dark blue kicker on a dark blue
    // page vanished (AI dry run 2026-09-23).
    // Mirror: publishedData.php#wm_generateTokenCss.
    const primaryRgb = primary ? parseColorToRgb(primary) : null;
    const pageAccent = primaryRgb
      && Math.round(contrastRatio(relativeLuminance(primaryRgb), relativeLuminance(parseColorToRgb(page))) * 100) / 100 >= 3
      ? primary : contrastTextColor(page);
    lines.push(`--wm-color-page-accent: ${pageAccent};`);
  }

  if ((t?.radius && RADIUS[t.radius] !== undefined) || isValidRadiusCustom(t?.radiusCustom)) {
    const base = isValidRadiusCustom(t?.radiusCustom) ? `${t.radiusCustom}px` : RADIUS[t.radius];
    lines.push(`--wm-radius: ${base};`);
  }

  const radiusOverrideVars = [
    ['button', '--wm-radius-button'],
    ['card', '--wm-radius-card'],
    ['image', '--wm-radius-image'],
    ['box', '--wm-radius-box'],
  ];
  radiusOverrideVars.forEach(([key, cssVar]) => {
    const val = t?.radiusOverrides?.[key];
    // A stage only as a string (mirrors the PHP guard is_string: a malformed
    // array like ['l'] must not coerce to a lookup key). A number is px
    // (Paket 7 E2), JS-only: the PHP fallback grafts the line instead.
    const value = radiusOverrideValue(val);
    if (value) lines.push(`${cssVar}: ${value};`);
  });

  if (t?.shadow && SHADOW[t.shadow] !== undefined) lines.push(`--wm-shadow: ${SHADOW[t.shadow]};`);
  // JS-only (Paket 7 E1), grafted by the PHP fallback like --wbmg-btn-pad.
  if (isValidCardShadow(t?.cardShadow)) lines.push(`--wm-shadow-2: ${cardShadowCss(resolveCardShadow(t))};`);

  // Read by button-kit.scss (.wbmg-btn--base). Not in the frozen PHP fallback
  // wm_generateTokenCss — only the push-down fallback path misses it.
  if (isValidButtonPadding(t?.buttonPadding)) {
    lines.push(`--wbmg-btn-pad: ${t.buttonPadding.y}px ${t.buttonPadding.x}px;`);
  }

  if (t?.typography?.enabled === true) lines.push(...emitTypographyLines(t.typography));
  const abgeleitet = derivedTokenCss(ci);
  lines.push(...abgeleitet.root);

  const bereichRegel = abgeleitet.bereich.length ? `${BEREICH_REGEL_SELEKTOR} { ${abgeleitet.bereich.join(' ')} }` : '';
  // Only Bereich lines (e.g. just a card style): the rule alone, like PHP's append on an empty string.
  if (!lines.length) return bereichRegel;
  const regeln = t?.typography?.enabled === true ? [...roleStyleRules(t.typography), ...customStyleRules(t.typography)] : [];
  // Only a magazine with a page colour gets the page rule; the theme leaves
  // .pgs-article alone. Doubled class beats user.scss `.pgs-article { color }`.
  // Mirror: publishedData.php#wm_generateTokenCss.
  if (page && parseColorToRgb(page) !== null) regeln.unshift(PAGE_RULE);
  if (bereichRegel) regeln.unshift(bereichRegel);
  return `:root {\n  ${lines.join('\n  ')}\n}${regeln.map((r) => `\n${r}`).join('')}`;
}

// --- CI from a website (spec 2026-10-03 CI von Webseite §6.3, §8, C7/C8) ---
// The only paths the import may write, and the one check for them. Built from
// the guards above, so "valid" means the same here as in the CI view and the
// emitter. Runs in the server (seiten-kern bundle) and in the admin.

const IMPORT_GRUPPEN = {
  farben: ['_customColor.0', '_customColor.1', '_additionalColors',
    ...['surface', 'page', 'ink', 'muted', 'line', 'darkSurface'].map((k) => `designTokens.colors.${k}`),
    'designTokens.autoFarben'],
  schriften: ['displayFont', 'displayFontGeneric', 'textFont', 'textFontGeneric']
    .map((k) => `designTokens.typography.${k}`),
  typografie: [
    ...['displayStyle', 'displayStretch', 'displayTransform'].map((k) => `designTokens.typography.${k}`),
    ...TYPOGRAPHY_ROLES.flatMap((r) => ['min', 'max', 'lineHeight', 'ls'].map((k) => `designTokens.typography.overrides.${r}.${k}`)),
    ...ROLE_STYLE_ROLES.flatMap((r) => ['staerke', 'kursiv', 'gross', 'schrift'].map((k) => `designTokens.typography.roleStyles.${r}.${k}`)),
    'designTokens.typography.customStyles',
    'designTokens.linkStyle',
  ],
  formen: ['designTokens.radius', 'designTokens.radiusCustom',
    ...['button', 'card', 'image', 'box'].map((k) => `designTokens.radiusOverrides.${k}`),
    'designTokens.cardShadow', 'designTokens.cardStyle'],
  abstaende: ['designTokens.spacing', 'designTokens.contentWidth'],
  knoepfe: ['designTokens.buttonPadding'],
  logo: ['logo.image', 'favicon.image'],
};

/** Every CI path the import may write, with its group in the review view (§9.2). */
export const CI_IMPORT_PFADE = Object.freeze(Object.entries(IMPORT_GRUPPEN)
  .flatMap(([gruppe, pfade]) => pfade.map((pfad) => Object.freeze({ pfad, gruppe }))));
/** The one knob the import may set (§8.4, C14). */
export const CI_IMPORT_STELLSCHRAUBEN = Object.freeze(['--wmk-picture-radius']);

// Results: wert null with status ok = delete the key (a style field's default is never stored, §8.3).
const gut = (wert, angepasst) => ({ status: angepasst ? 'angepasst' : 'ok', wert, meldung: angepasst ? 'grenze' : null });
const weg = (meldung) => ({ status: 'verworfen', wert: null, meldung });
const runden = (v, stellen) => Number(v.toFixed(stellen));

/** A number clamped into [min, max] and rounded (C8): out of bounds = angepasst, no number = verworfen. */
function zahlPruefen(v, min, max, stellen, gueltig) {
  if (!Number.isFinite(v)) return weg('typ');
  const n = runden(Math.min(max, Math.max(min, v)), stellen);
  return gueltig(n) ? gut(n, v < min || v > max) : weg('ungueltig');
}
function farbePruefen(v) {
  const c = safeColor(v);
  return c && parseColorToRgb(c) ? gut(c) : weg('farbe');
}
function listePruefen(werte, standard) {
  return (v) => {
    if (!werte.includes(v)) return weg('liste');
    return gut(v === standard ? null : v);
  };
}
function boolPruefen(v) {
  return v === true || v === false ? gut(v) : weg('typ');
}
function schriftPruefen(v) {
  const name = safeFontName(v);
  return name ? gut(name) : weg('schrift');
}
// Objects whose numbers are clamped one by one, then checked by the emitter's own guard.
function teileKlammern(v, grenzen, gueltig, extra) {
  if (!(v instanceof Object) || Array.isArray(v)) return weg('typ');
  const teile = Object.entries(grenzen).map(([k, max]) => [k, zahlPruefen(v[k], 0, max, 0, () => true)]);
  const kaputt = teile.find(([, r]) => r.status === 'verworfen');
  if (kaputt) return kaputt[1];
  const neu = { ...extra, ...Object.fromEntries(teile.map(([k, r]) => [k, r.wert])) };
  return gueltig(neu) ? gut(neu, teile.some(([, r]) => r.status === 'angepasst')) : weg('ungueltig');
}
const BILD_RE = /^https:\/\/[^\s"'<>()\\]+\.(svg|png|webp|jpe?g|gif|ico)(\?[^\s"'<>()\\]*)?$/i;
// String(v) === v: only a string primitive passes (no typeof, anti-slop).
const bildPruefen = (v) => (String(v) === v && BILD_RE.test(v) ? gut(v) : weg('bild'));

const IMPORT_PRUEFER = new Map([
  ['_customColor.0', farbePruefen],
  ['_customColor.1', farbePruefen],
  ['_additionalColors', (v) => {
    if (!Array.isArray(v) || !v.length) return weg('typ');
    const farben = v.map(safeColor);
    if (farben.some((c) => !c || !parseColorToRgb(c))) return weg('farbe');
    return gut(farben.slice(0, 3), farben.length > 3);
  }],
  ...['surface', 'page', 'ink', 'muted', 'line', 'darkSurface'].map((k) => [`designTokens.colors.${k}`, farbePruefen]),
  ['designTokens.autoFarben', boolPruefen],
  ['designTokens.typography.displayFont', schriftPruefen],
  ['designTokens.typography.textFont', schriftPruefen],
  ['designTokens.typography.displayFontGeneric', listePruefen(FONT_GENERIC_WHITELIST)],
  ['designTokens.typography.textFontGeneric', listePruefen(FONT_GENERIC_WHITELIST)],
  ['designTokens.typography.displayStyle', listePruefen(Object.keys(DISPLAY_STYLE))],
  ['designTokens.typography.displayStretch', listePruefen(Object.keys(DISPLAY_STRETCH))],
  ['designTokens.typography.displayTransform', listePruefen(Object.keys(DISPLAY_TRANSFORM))],
  ...TYPOGRAPHY_ROLES.flatMap((r) => {
    const p = `designTokens.typography.overrides.${r}`;
    // Marketing sizes are no magazine sizes (§8.2): display/h1/h2 at most 1.5 x the curve.
    const groesse = ['display', 'h1', 'h2'].includes(r) ? runden(TYPE_CURVE[r].max * 1.5, 4) : TYPE_LIMITS.size.max;
    const anker = (v) => zahlPruefen(v, TYPE_LIMITS.size.min, groesse, 4, isValidTypeAnchor);
    return [
      [`${p}.min`, anker],
      [`${p}.max`, anker],
      [`${p}.lineHeight`, (v) => zahlPruefen(v, TYPE_LIMITS.lineHeight.min, TYPE_LIMITS.lineHeight.max, 4, isValidLineHeightOverride)],
      [`${p}.ls`, (v) => zahlPruefen(v, TYPE_LIMITS.ls.min, TYPE_LIMITS.ls.max, 4, isValidLetterSpacing)],
    ];
  }),
  ...ROLE_STYLE_ROLES.flatMap((r) => {
    const p = `designTokens.typography.roleStyles.${r}`;
    return [
      // A measured weight (variable fonts: any 1..1000) goes to the nearest listed one.
      [`${p}.staerke`, (v) => {
        const n = zahlPruefen(v, 100, 900, 0, () => true);
        if (n.status === 'verworfen') return n;
        const w = Math.round(n.wert / 100) * 100;
        return TYPE_WEIGHTS.includes(w) ? gut(w, w !== v) : weg('ungueltig');
      }],
      [`${p}.kursiv`, boolPruefen],
      [`${p}.gross`, boolPruefen],
      [`${p}.schrift`, schriftPruefen],
    ];
  }),
  // The emitter's own clean-up; anything it dropped makes the value angepasst.
  ['designTokens.typography.customStyles', (v) => {
    if (!Array.isArray(v)) return weg('typ');
    const neu = cleanCustomStyles(v);
    return gut(neu, JSON.stringify(neu) !== JSON.stringify(v));
  }],
  ['designTokens.linkStyle', listePruefen(Object.keys({ unterstrichen: 1, ...STIL_ZEILEN.linkStyle }), 'unterstrichen')],
  ['designTokens.cardStyle', listePruefen(Object.keys({ schatten: 1, ...STIL_ZEILEN.cardStyle }), 'schatten')],
  ['designTokens.radius', listePruefen(Object.keys(RADIUS))],
  ['designTokens.radiusCustom', (v) => zahlPruefen(v, 0, 100, 0, isValidRadiusCustom)],
  ...['button', 'card', 'image', 'box'].map((k) => [`designTokens.radiusOverrides.${k}`,
    (v) => (String(v) === v ? listePruefen(Object.keys(RADIUS))(v) : zahlPruefen(v, 0, 100, 0, isValidRadiusCustom))]),
  ['designTokens.cardShadow', (v) => {
    const color = v instanceof Object ? safeColor(v.color) : null;
    if (!color || !parseColorToRgb(color)) return weg('farbe');
    return teileKlammern(v, CARD_SHADOW_MAX, isValidCardShadow, { color });
  }],
  ['designTokens.buttonPadding', (v) => teileKlammern(v, BUTTON_PADDING_MAX, isValidButtonPadding)],
  ['designTokens.spacing', listePruefen(Object.keys(SPACING))],
  ['designTokens.contentWidth', (v) => zahlPruefen(v, 480, 2000, 0, isValidContentWidth)],
  ['logo.image', bildPruefen],
  ['favicon.image', bildPruefen],
  // §8.4: whole px 0-100, written as "<n>px".
  ['--wmk-picture-radius', (v) => {
    const n = String(v) === v && /^-?\d+(\.\d+)?px$/.test(v) ? parseFloat(v) : v;
    const r = zahlPruefen(n, 0, 100, 0, () => true);
    return r.status === 'verworfen' ? r : { ...r, wert: `${r.wert}px` };
  }],
]);

/**
 * Checks one imported value: { status: 'ok' | 'angepasst' | 'verworfen', wert, meldung }.
 * Numbers out of bounds are clamped (angepasst), anything else invalid is
 * discarded. wert null with status ok means "delete the key" (style default).
 * meldung is a code: pfad, typ, farbe, liste, schrift, bild, ungueltig, grenze.
 */
export function ciImportPruefen(pfad, wert) {
  const pruefer = IMPORT_PRUEFER.get(pfad);
  return pruefer ? pruefer(wert) : weg('pfad');
}
